import { useState, useEffect } from 'react';
import { statisticsApi } from '../../api/statistics.api';
import Loading from '../../components/common/Loading';

const Statistics = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>({
    brands: [],
    customersByCity: [],
    salesSummary: null,
    repairStatus: [],
    revenue: [],
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setIsLoading(true);
      const [brands, customersByCity, salesSummary, repairStatus, revenue] = await Promise.all([
        statisticsApi.getBrands().catch(() => []),
        statisticsApi.getCustomersByCity().catch(() => []),
        statisticsApi.getSalesSummary().catch(() => null),
        statisticsApi.getRepairStatus().catch(() => []),
        statisticsApi.getRevenue().catch(() => []),
      ]);

      setStats({ brands, customersByCity, salesSummary, repairStatus, revenue });
      setError(null);
    } catch (err: any) {
      setError(err.message || 'İstatistikler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loading text="İstatistikler yükleniyor..." />;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">İstatistikler</h1>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Sales Summary */}
        {stats.salesSummary && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Satış Özeti</h2>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                Toplam Satış:{' '}
                <span className="font-semibold">{stats.salesSummary.totalSales}</span>
              </p>
              <p className="text-sm text-gray-600">
                Toplam Gelir:{' '}
                <span className="font-semibold">
                  ₺{stats.salesSummary.totalRevenue?.toFixed(2) || '0.00'}
                </span>
              </p>
              <p className="text-sm text-gray-600">
                Ortalama Satış:{' '}
                <span className="font-semibold">
                  ₺{stats.salesSummary.averageSaleValue?.toFixed(2) || '0.00'}
                </span>
              </p>
            </div>
          </div>
        )}

        {/* Brand Statistics */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Marka İstatistikleri</h2>
          <div className="space-y-2">
            {stats.brands.slice(0, 5).map((brand: any, index: number) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-gray-600">{brand.brand || 'Bilinmeyen'}</span>
                <span className="font-semibold">{brand.count}</span>
              </div>
            ))}
            {stats.brands.length === 0 && (
              <p className="text-sm text-gray-500">Veri bulunmamaktadır</p>
            )}
          </div>
        </div>

        {/* Customers by City */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Şehirlere Göre Müşteriler</h2>
          <div className="space-y-2">
            {stats.customersByCity.slice(0, 5).map((city: any, index: number) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-gray-600">{city.city || 'Bilinmeyen'}</span>
                <span className="font-semibold">{city.count}</span>
              </div>
            ))}
            {stats.customersByCity.length === 0 && (
              <p className="text-sm text-gray-500">Veri bulunmamaktadır</p>
            )}
          </div>
        </div>

        {/* Repair Status */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Onarım Durumu</h2>
          <div className="space-y-2">
            {stats.repairStatus.map((status: any, index: number) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-gray-600">{status.status}</span>
                <span className="font-semibold">{status.count}</span>
              </div>
            ))}
            {stats.repairStatus.length === 0 && (
              <p className="text-sm text-gray-500">Veri bulunmamaktadır</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
